import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { DotsSixVertical, Trash, Barbell } from '@phosphor-icons/react';
import { WORKOUT_BUILDER } from '@/constants/testIds';

export function SortableExerciseRow({
    id,
    index,
    exercise,
    entry,
    onChange,
    onRemove,
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <li
            ref={setNodeRef}
            style={style}
            data-testid={`${WORKOUT_BUILDER.exerciseRow}-${index}`}
            className="glass-card rounded-2xl p-4 sm:p-5"
        >
            <div className="flex items-start gap-3">
                <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    data-testid={`${WORKOUT_BUILDER.dragHandle}-${index}`}
                    aria-label="Drag to reorder"
                    className="shrink-0 grid place-items-center w-9 h-9 rounded-xl border border-foreground/10 bg-foreground/5 text-muted-foreground hover:text-foreground hover:bg-foreground/10 cursor-grab active:cursor-grabbing touch-none"
                >
                    <DotsSixVertical size={16} weight="bold" />
                </button>

                <span className="font-mono text-xs text-muted-foreground tabular-nums pt-2.5 shrink-0 w-6">
                    {String(index + 1).padStart(2, '0')}
                </span>

                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <Barbell size={14} weight="bold" className="text-muted-foreground shrink-0" />
                        <h3 className="font-display font-bold tracking-tight truncate">
                            {exercise?.name || 'Unknown exercise'}
                        </h3>
                    </div>
                    <p className="uppercase-mono text-muted-foreground truncate">
                        {exercise?.category || '—'} ·{' '}
                        {exercise?.equipment || '—'}
                    </p>
                </div>

                <button
                    type="button"
                    onClick={onRemove}
                    data-testid={`${WORKOUT_BUILDER.removeExerciseBtn}-${index}`}
                    aria-label="Remove exercise"
                    className="shrink-0 grid place-items-center w-9 h-9 rounded-xl border border-foreground/10 bg-foreground/5 hover:bg-[#FF4D4D]/15 hover:border-[#FF4D4D]/40 hover:text-[#FF4D4D] active:scale-[0.96] transition-all"
                >
                    <Trash size={15} weight="bold" />
                </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
                <NumberField
                    label="Target sets"
                    testId={`${WORKOUT_BUILDER.targetSetsInput}-${index}`}
                    value={entry.target_sets}
                    min={1}
                    max={20}
                    onChange={(v) => onChange({ ...entry, target_sets: v })}
                />
                <NumberField
                    label="Target reps"
                    testId={`${WORKOUT_BUILDER.targetRepsInput}-${index}`}
                    value={entry.target_reps}
                    min={1}
                    max={99}
                    onChange={(v) => onChange({ ...entry, target_reps: v })}
                />
            </div>

            <label className="block mt-3">
                <span className="uppercase-mono text-muted-foreground block mb-2">
                    Notes
                </span>
                <input
                    type="text"
                    data-testid={`${WORKOUT_BUILDER.entryNotesInput}-${index}`}
                    value={entry.notes || ''}
                    onChange={(e) =>
                        onChange({ ...entry, notes: e.target.value })
                    }
                    placeholder="Tempo, cues, accessories…"
                    className="w-full h-11 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors placeholder:text-muted-foreground/70"
                />
            </label>
        </li>
    );
}

function NumberField({ label, value, onChange, testId, min, max }) {
    return (
        <label className="block min-w-0">
            <span className="uppercase-mono text-muted-foreground block mb-2">
                {label}
            </span>
            <input
                type="number"
                inputMode="numeric"
                data-testid={testId}
                value={value}
                min={min}
                max={max}
                onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (Number.isFinite(n)) onChange(n);
                    else if (e.target.value === '') onChange('');
                }}
                className="w-full h-11 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors font-mono text-lg font-bold tabular-nums"
            />
        </label>
    );
}
